/**
 * This component provide an unstyled input keeping tracks of the previous value
 * when typing to revert back when clicking on escape.
 *
 * It provides an imperative handle to focus the input from the parent component
 */

import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { KeyboardKeys } from '../../assets/enums/keyboard-keys.enum';

import styles from './history-input.module.scss';

export interface HistoryInputRef {
  focus: () => void;
}

interface HistoryInputProps {
  value: string;
  onChange: (value: string) => void;

  /**
   * Imperative handle to focus the input from a parent
   */
  ref?: React.Ref<HistoryInputRef>;

  type?: HTMLInputElement['type'];

  min?: number;
}

export const HistoryInput: React.FC<HistoryInputProps> = ({
  value,
  onChange,
  type = 'text',
  min,
  ref,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [_value, setValue] = useState(value);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
    }),
    [],
  );

  useEffect(() => {
    setValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleOnBlur = async () => {
    if (_value !== value) {
      onChange(_value);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KeyboardKeys.Escape) {
      setValue(value);
      return;
    }

    if (e.key === KeyboardKeys.Enter && value !== _value) {
      e.preventDefault();
      inputRef!.current?.blur();
      return;
    }
  };

  return (
    <input
      ref={inputRef}
      className={styles.input}
      value={_value}
      onChange={handleChange}
      onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
      type={type}
      min={min}
      onKeyDown={handleKeyDown}
      onBlur={handleOnBlur}
    />
  );
};
